/**
 * useVoiceInput
 *
 * React hook for voice-to-text using MediaRecorder + Gemini transcription.
 * Works in both regular browser and immersive WebXR (where SpeechRecognition
 * is broken on Quest).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { transcribeAudio } from './transcribe-audio';

export type VoiceState = 'idle' | 'recording' | 'transcribing';

/** Minimum audio blob size (bytes) that we will attempt to transcribe. */
export const MIN_AUDIO_BYTES = 1000;
/** Minimum recording duration (ms) that we will attempt to transcribe. */
export const MIN_AUDIO_MS = 500;

/** True when an audio capture is too short/quiet to bother transcribing. */
export function isAudioTooShort(byteSize: number, durationMs: number): boolean {
    return byteSize < MIN_AUDIO_BYTES || durationMs < MIN_AUDIO_MS;
}

export interface UseVoiceInputReturn {
    /** Current state of the voice input */
    state: VoiceState;
    /** Start recording audio. Resolves once the mic is acquired (first call) and recording is active. */
    startRecording: () => Promise<void>;
    /** Stop recording and transcribe — returns the transcript or null */
    finishRecording: () => Promise<string | null>;
    /** Cancel recording without transcribing */
    cancelRecording: () => void;
    /** Whether voice input is available (has mic permission) */
    available: boolean;
    /** Error message if something went wrong */
    error: string | null;
}

export function useVoiceInput(): UseVoiceInputReturn {
    const [state, setState] = useState<VoiceState>('idle');
    const [available, setAvailable] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mediaStreamRef = useRef<MediaStream | null>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const startTimeRef = useRef(0);

    // Feature detection — don't acquire the mic until the user asks to record.
    // Acquiring on mount prompts for permission in embeds where the feature may
    // never be used.
    useEffect(() => {
        setAvailable(typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia);
        return () => {
            if (mediaStreamRef.current) {
                for (const track of mediaStreamRef.current.getTracks()) {
                    track.stop();
                }
                mediaStreamRef.current = null;
            }
        };
    }, []);

    const startRecording = useCallback(async () => {
        if (!mediaStreamRef.current) {
            try {
                mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
                    audio: { echoCancellation: true, noiseSuppression: true },
                });
            } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                setError(`Microphone not available: ${msg}`);
                return;
            }
        }

        setError(null);
        chunksRef.current = [];
        startTimeRef.current = performance.now();

        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : MediaRecorder.isTypeSupported('audio/webm')
                ? 'audio/webm'
                : 'audio/mp4';

        let recorder: MediaRecorder;
        try {
            recorder = new MediaRecorder(mediaStreamRef.current, { mimeType });
        } catch {
            recorder = new MediaRecorder(mediaStreamRef.current);
        }

        recorder.ondataavailable = (ev) => {
            if (ev.data.size > 0) {
                chunksRef.current.push(ev.data);
            }
        };

        recorder.start(250);
        recorderRef.current = recorder;
        setState('recording');
        console.log('[VoiceInput] Recording started');
    }, []);

    const cancelRecording = useCallback(() => {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            try { recorderRef.current.stop(); } catch { /* already stopped */ }
        }
        recorderRef.current = null;
        chunksRef.current = [];
        setState('idle');
    }, []);

    const finishRecording = useCallback(async (): Promise<string | null> => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
            setState('idle');
            return null;
        }

        const durationMs = performance.now() - startTimeRef.current;
        const mimeType = recorder.mimeType;

        // The ondataavailable handler registered in startRecording stays in
        // place and captures the final chunk fired by stop() — don't rebind
        // it, that introduces a race where the final chunk can land on the
        // old handler before the new one is installed.
        const audioBlob = await new Promise<Blob>((resolve) => {
            recorder.onstop = () => {
                resolve(new Blob(chunksRef.current, { type: mimeType }));
            };
            recorder.stop();
        });

        recorderRef.current = null;
        chunksRef.current = [];

        if (isAudioTooShort(audioBlob.size, durationMs)) {
            console.log('[VoiceInput] Recording too short');
            setState('idle');
            return null;
        }

        setState('transcribing');

        try {
            const transcript = await transcribeAudio(audioBlob, mimeType);
            setState('idle');

            if (!transcript) {
                console.log('[VoiceInput] No transcript from audio');
                return null;
            }

            console.log('[VoiceInput] Transcript:', transcript);
            return transcript;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error('[VoiceInput] Transcription error:', msg);
            setError(msg);
            setState('idle');
            return null;
        }
    }, []);

    return { state, startRecording, finishRecording, cancelRecording, available, error };
}
